import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiInstance from "../../api/api";
import apiEndpoints from "../../utils/api/endpoints";

interface BicycleGameState {
  loading: boolean;
  error: string | null;
  lastSavedScore: number;
  user: string | null;
  totalAllScore: number;
  lastValidUserScore: number; 
}

interface UserDetails {
  unique_id: string;
  firstName: string | null;
  lastName: string | null;
  birthDate: string | null;
  phoneNumber: string | null;
}

const initialState: BicycleGameState = {
  loading: false,
  error: null,
  lastSavedScore: 0,
  user: localStorage.getItem('bicycleGameUserId'),
  totalAllScore: 0,
  lastValidUserScore: 0
};

export const saveGameScore = createAsyncThunk(
  'bicycleGame/saveScore',
  async ({ score, unique_id }: { score: number; unique_id: string }) => {
    try {
      const response = await apiInstance.post(apiEndpoints.bicycleGameStore, {
        score,
        unique_id
      });

      if (response.data.success) {
        return response.data;
      } else {
        throw new Error(response.data.message || 'Nu s-a putut salva scorul.');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Nu s-a putut salva scorul. Încearcă din nou.';
      throw new Error(errorMessage);
    }
  }
);

export const updateGameScore = createAsyncThunk(
  'bicycleGame/updateScore',
  async ({ score, unique_id }: { score: number; unique_id: string }) => {
    try {
      const response = await apiInstance.put(apiEndpoints.bicycleGameUpdate, {
        score,
        unique_id
      });

      if (response.data.success) {
        return response.data;
      } else {
        throw new Error(response.data.message || 'Nu s-a putut actualiza scorul.');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Nu s-a putut actualiza scorul. Încearcă din nou.';
      throw new Error(errorMessage);
    }
  }
);


export const fetchTotalScore = createAsyncThunk(
  'bicycleGame/fetchTotalTeamScore',
  async () => {
    try {
      const response = await apiInstance.get(`${apiEndpoints.bicycleGameTotal}`);
      if (response.data.success) {
        // Asigură că returnăm un număr
        return Number(response.data.totalAllScore) || 0;
      }
      return 0;
    } catch (error: any) {
      return 0;
    }
  }
);

export const fetchUserScore = createAsyncThunk(
  'bicycleGame/fetchUserScore',
  async (unique_id: string) => {
    try {
      const response = await apiInstance.get(`${apiEndpoints.bicycleGameScore}/${unique_id}`);
      
      if (response.data.success) {
        const score = Number(response.data.score);
        return score;
      }
      throw new Error('Failed to fetch user score');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Could not fetch user score';
      throw new Error(errorMessage);
    }
  }
);

export const generateUserId = createAsyncThunk(
  'bicycleGame/generateUserId',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiInstance.post(`${apiEndpoints.bicycleGameGenerateId}`);

      if (response.data?.success && response.data?.unique_id) {
        return response.data.unique_id;
      }
      
      return rejectWithValue(response.data?.message || 'Failed to generate user ID: Invalid response format');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message 
        || error.message 
        || 'Could not generate user ID';
      return rejectWithValue(errorMessage);
    }
  }
);

export const saveUserDetails = createAsyncThunk(
  'bicycleGame/saveUserDetails',
  async (userDetails: UserDetails) => {
    try {
      const response = await apiInstance.post(apiEndpoints.bicycleGameUserDetails, userDetails);

      if (response.data.success) {
        return response.data;
      } else {
        throw new Error(response.data.message || 'Nu s-au putut salva detaliile.');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message;
      throw new Error(errorMessage);
    }
  }
);


const bicycleGameSlice = createSlice({
  name: 'bicycleGame',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUserId: (state, action) => {
      state.user = action.payload;
      localStorage.setItem('bicycleGameNickname', action.payload);
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(saveGameScore.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveGameScore.fulfilled, (state, action) => {
        state.loading = false;
        state.lastSavedScore = action.payload.score;
        state.error = null;
      })
      .addCase(saveGameScore.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Nu s-a putut salva scorul';
      })
      .addCase(updateGameScore.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateGameScore.fulfilled, (state, action) => {
        state.loading = false;
        state.lastSavedScore = action.payload.score;
        state.error = null;
      })
      .addCase(updateGameScore.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Nu s-a putut actualiza scorul';
      })
      .addCase(fetchTotalScore.fulfilled, (state, action) => {
        state.totalAllScore = Number(action.payload);
      })
      .addCase(fetchTotalScore.rejected, (state, action) => {
        state.error = action.error.message || 'Nu s-a putut prelua scorul total';
      })
      .addCase(fetchUserScore.fulfilled, (state, action) => {
        state.lastValidUserScore = Number(action.payload);
      })
      .addCase(generateUserId.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        localStorage.setItem('bicycleGameUserId', action.payload);
      })
      .addCase(generateUserId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to generate user ID';
      })
      .addCase(saveUserDetails.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(saveUserDetails.rejected, (state, action) => {
        state.error = action.error.message || 'Nu s-au putut salva detaliile';
      });
  },
});

export const { clearError, setUserId } = bicycleGameSlice.actions;
export default bicycleGameSlice.reducer;